// Which version of each meetstandaard we serve, and how to address it.
//
// PINNING IS DELIBERATE. A published version is immutable and keeps being
// served indefinitely, so an organisation that adopted effects at 0.9 keeps
// 0.9 even after 1.0 ships. Following "newest" would silently change the
// wording — and the positive/negative framing — behind answers that were
// already given, which is the exact failure that damaged us before.
//
// Bumping a version here is a migration decision, not a config tweak: existing
// adopted effects carry their own StandaardVersion and are NOT moved by
// changing this constant. New adoptions get the new version; old ones keep
// theirs until someone migrates them deliberately.
//
// Mirrored by functions/data/standaardVersions.js — keep in sync. This file is
// the source; the backend copy exists because the nightly platform total reads
// the same versioned documents. Parity is pinned by the mirrored tests.
//
// AI-CONTEXT: See docs/pitfalls.md#api-meetstandaard-same-name-sector-variants

const API_BASE = 'https://us-central1-meetstandaard-api.cloudfunctions.net'

// The resource name appears twice in the path on purpose: one function serves
// every sector, with the sector as a path segment.
const MEETSTANDAARD_PATH = `${API_BASE}/meetstandaard/api/v1/meetstandaard`

/**
 * Sector slug → pinned version id.
 *
 * Only sectors served by the versioned pipeline appear here. gelijke-kansen
 * still comes from the legacy /database catalogue, which has no versions at
 * all, until the api team migrates it. A pinned sector is filtered out of the
 * /database catalogue (withoutPinnedSectors), so it is never offered twice.
 *
 * @type {Object<string, string>}
 */
const PINNED_STANDAARD_VERSIONS = {
    // 0.12 (2026-10-18, meetstandaard-api 2b5e6e7): eenheid per niveau en
    // situatieschets niveau 1; stellingen en bedragen gelijk aan 0.11.
    energiearmoede: '0.12',
    // 0.3 (2026-10-20, meetstandaard-api 0a7e9ff) voegt drie effecten toe:
    // dagelijks functioneren, ervaren invloed op de buurt en vertrouwen in
    // instanties. Buurtcohesie volgt energiearmoede 0.13 (lagere bedragen).
    wijkbedrijven: '0.3',
    // 1.1: de vastgestelde stellingteksten. 1.0 (2026-10-20, meetstandaard-api
    // 5aa7932) had bij zeven stellingen B1-teksten die een keuze van
    // energiearmoede waren (meetstandaard-team 2026-10-22); 1.0 is daarom nooit
    // gepind. 1.1 gepubliceerd 2026-10-28 (meetstandaard-api 48eb25c). Daarvoor
    // kwam arbeidsparticipatie uit /database met het opgegeven stempel 0.9
    // (legacyStandaardVersions.js).
    // AI-CONTEXT: See docs/features/arbeidsparticipatie-1-0.md
    arbeidsparticipatie: '1.1',
}

/**
 * Sector slug → earlier versions that existing adoptions still carry.
 *
 * Moving the pin does not move what was adopted under the old one: those rows
 * keep their StandaardVersion and their wording. Whether they keep their
 * amounts too depends on EQUIVALENT_STANDAARD_VERSIONS: a superseded version
 * that is not equivalent to a later one is valued at its own amounts
 * (methodiek impact-per-euro §5.3).
 *
 * A version leaves this list only when no row carries it any more.
 *
 * @type {Object<string, Array<string>>}
 */
const SUPERSEDED_STANDAARD_VERSIONS = {
    energiearmoede: ['0.9', '0.10', '0.11'],
    wijkbedrijven: ['0.1', '0.2'],
    // De regels op een stelling die stopt blijven op 0.9 staan. 1.0 staat hier
    // niet: geen regel is ooit onder 1.0 gestempeld.
    arbeidsparticipatie: ['0.9'],
}

/**
 * @returns {Array<string>} sectors served from the versioned pipeline
 */
const pinnedSectors = () => Object.keys(PINNED_STANDAARD_VERSIONS)

/**
 * Sector slug → version → the later version whose statements it counts as.
 *
 * Besluit product owner, 2026-10-13: energiearmoede 0.10 herformuleert de
 * stellingen van 0.9 naar B1-taal en geldt als dezelfde stelling. 0.11 heeft
 * dezelfde stellingen als 0.10 (alleen de overlapregel en drie gecorrigeerde
 * tarieven) en geldt daarom ook als dezelfde versie. Antwoorden op 0.9, 0.10 en
 * 0.11 lopen door als één reeks, en alle effecten worden gewaardeerd tegen de
 * bedragen van 0.11 — ook de geadopteerde onder 0.9 en 0.10.
 *
 * 2026-10-18: energiearmoede 0.12 heeft dezelfde stellingen en bedragen als
 * 0.11 (alleen de eenheid per monetariseringsniveau en de situatieschets van
 * niveau 1 erbij), dus 0.11 telt als 0.12 en de reeks loopt door.
 *
 * Besluit product owner, 2026-10-16: wijkbedrijven 0.2 heeft dezelfde
 * stellingen als 0.1 voor de dertien bestaande effecten (alleen de
 * cohesielaag erbij en een herberekende GGZ-post bij sociale contacten), dus
 * 0.1 telt als 0.2 en wordt gewaardeerd tegen de bedragen van 0.2.
 *
 * Besluit product owner, 2026-10-21: wijkbedrijven 0.3 heeft dezelfde
 * stellingen als 0.2 voor de negentien bestaande effecten (drie effecten erbij;
 * buurtcohesie volgt energiearmoede 0.13 met lagere bedragen op niveau 1-3).
 * Er zijn nog geen metingen op 0.2, dus 0.2 telt als 0.3 en wordt gewaardeerd
 * tegen de bedragen van 0.3.
 *
 * Besluit gebruiker, 2026-09-22 en 2026-10-20: arbeidsparticipatie 1.1 meet
 * met elke doorlopende stelling hetzelfde construct als 0.9 (migratiekaart
 * 0.9 → 1.0/1.1), dus 0.9 telt als 1.1 en alles wordt gewaardeerd tegen de
 * bedragen van 1.1. Stellingen die stoppen houden hun 0.9-stempel; de
 * gelijkwaardigheid voorkomt dat een effect met zulke regels 'gemengde
 * versies' wordt.
 *
 * The stamp on a row keeps saying which wording was asked; this map says what
 * that wording counts as. A version missing here is its own version.
 *
 * @type {Object<string, Object<string, string>>}
 */
const EQUIVALENT_STANDAARD_VERSIONS = {
    energiearmoede: { '0.9': '0.10', '0.10': '0.11', '0.11': '0.12' },
    wijkbedrijven: { '0.1': '0.2', '0.2': '0.3' },
    arbeidsparticipatie: { '0.9': '1.1' },
}

/**
 * The version a stamped version counts as, following the equivalence map.
 *
 * @param {string} sector
 * @param {string} version
 * @returns {string}
 */
const canonicalStandaardVersion = (sector, version) => {
    const own = Object.prototype.hasOwnProperty
    const map = own.call(EQUIVALENT_STANDAARD_VERSIONS, sector) ? EQUIVALENT_STANDAARD_VERSIONS[sector] : {}
    let current = version
    const seen = new Set()
    while (current && own.call(map, current) && !seen.has(current)) {
        seen.add(current)
        current = map[current]
    }
    return current
}

/**
 * Every [sector, version] whose amounts are needed: the canonical version of
 * each version still in use, the pin last. An equivalent version needs no
 * document of its own — it is valued at the version it counts as.
 *
 * @returns {Array<[string, string]>}
 */
const monetisedStandaardVersions = () => Object.entries(PINNED_STANDAARD_VERSIONS)
    .flatMap(([sector, pinned]) => {
        const versions = [...(SUPERSEDED_STANDAARD_VERSIONS[sector] || []), pinned]
            .map((version) => canonicalStandaardVersion(sector, version))
        return [...new Set(versions)]
            .sort((a, b) => (a === pinned) - (b === pinned))
            .map((version) => [sector, version])
    })

/**
 * URL of one immutable published version.
 *
 * Throws rather than falling back to the unpinned route: quietly fetching
 * "newest" is the failure mode this module exists to prevent.
 *
 * @param {string} sector
 * @param {string} version - An exact version id such as '0.9'
 * @returns {string}
 */
const standaardDocumentUrl = (sector, version) => {
    if (!sector) throw new Error('standaardDocumentUrl: sector is required')
    if (!version || version === 'latest') {
        throw new Error(`standaardDocumentUrl: an exact version is required for '${sector}', got '${version}'`)
    }
    return `${MEETSTANDAARD_PATH}/${sector}/${version}`
}

/**
 * URL listing the versions that exist upstream — for checking whether our pin
 * has fallen behind, never for fetching content.
 *
 * @param {string} sector
 * @returns {string}
 */
const standaardVersionsUrl = (sector) => {
    if (!sector) throw new Error('standaardVersionsUrl: sector is required')
    return `${MEETSTANDAARD_PATH}/${sector}/versions`
}

export {
    API_BASE,
    MEETSTANDAARD_PATH,
    PINNED_STANDAARD_VERSIONS,
    SUPERSEDED_STANDAARD_VERSIONS,
    EQUIVALENT_STANDAARD_VERSIONS,
    canonicalStandaardVersion,
    monetisedStandaardVersions,
    pinnedSectors,
    standaardDocumentUrl,
    standaardVersionsUrl,
}
